import { useNavigate } from "@tanstack/react-router";
import {
  CheckSquare,
  ChevronDown,
  ChevronRight,
  Flag,
  HelpCircle,
  Mail,
  MessageCircleQuestion,
  MessageSquare,
  Paperclip,
  Pencil,
  Square,
  Trash2,
  Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AuditFilesButton } from "@/components/audits/AuditFilesButton";
import { BlockAuditDialog } from "@/components/audits/BlockAuditDialog";
import { CompleteAuditDialog } from "@/components/audits/CompleteAuditDialog";
import { EditAuditAssigneeDialog } from "@/components/audits/EditAuditAssigneeDialog";
import { EditAuditTemplatesDialog } from "@/components/audits/EditAuditTemplatesDialog";
import { GuidanceRequestLog } from "@/components/audits/GuidanceRequestLog";
import { ItemUploadButton } from "@/components/audits/ItemUploadButton";
import { KickoffEmailDialog } from "@/components/audits/KickoffEmailDialog";
import { RequestGuidanceForAuditDialog } from "@/components/audits/RequestGuidanceForAuditDialog";
import { UnblockAuditDialog } from "@/components/audits/UnblockAuditDialog";
import { ProjectTeamDialog } from "@/components/projects/ProjectTeamDialog";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { ErrorState } from "@/components/shared/error-state";
import { RichTextEditor } from "@/components/shared/RichTextEditor";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { type AuditFileRow, openAuditFileDownload, useAuditFiles } from "@/hooks/use-audit-files";
import type { AuditItemNoteRow, AuditItemRow, AuditNoteRow } from "@/hooks/use-audits";
import {
  BLOCKED_BY_LABELS,
  useAudit,
  useAuditItemNotes,
  useCreateAuditItemNote,
  useCreateAuditNote,
  useDeleteAudit,
  useDeleteAuditItemNote,
  useDeleteAuditNote,
  useSetAuditStatus,
  useUpdateAuditItemNote,
  useUpdateAuditItems,
  useUpdateAuditNote,
} from "@/hooks/use-audits";
import { useCollapsedSections } from "@/hooks/use-collapsed-sections";
import { useProject } from "@/hooks/use-projects";
import { AUDIT_STATUS_BADGE_VARIANTS, AUDIT_STATUS_LABELS } from "@/lib/audit-status";
import { useSession } from "@/lib/auth-client";
import { formatDate, formatDateTime } from "@/lib/format";

interface AuditChecklistProps {
  auditId: string;
}

const ALL_STATUSES = ["assigned", "in_progress", "complete", "blocked"] as const;

export function AuditChecklist({ auditId }: AuditChecklistProps) {
  const { data: audit, isLoading, isError, refetch } = useAudit(auditId);
  const { data: session } = useSession();
  const { data: project } = useProject(audit?.projectId ?? "");
  const { data: auditFiles = [] } = useAuditFiles(auditId);
  const updateItems = useUpdateAuditItems(auditId);
  const setStatus = useSetAuditStatus(auditId);
  const deleteAudit = useDeleteAudit();
  const navigate = useNavigate();
  const { isSectionCollapsed, toggleSection } = useCollapsedSections(auditId, session?.user?.id);

  const [localChecked, setLocalChecked] = useState<Record<string, boolean>>({});
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [notesItemId, setNotesItemId] = useState<string | null>(null);
  const [teamOpen, setTeamOpen] = useState(false);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [unblockDialogOpen, setUnblockDialogOpen] = useState(false);
  const [guidanceDialogOpen, setGuidanceDialogOpen] = useState(false);
  const [kickoffDialogOpen, setKickoffDialogOpen] = useState(false);
  const [resendDialogOpen, setResendDialogOpen] = useState(false);
  const [editTemplatesOpen, setEditTemplatesOpen] = useState(false);
  const [editAssigneeOpen, setEditAssigneeOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<(typeof ALL_STATUSES)[number] | null>(null);
  const [pendingResolutionNote, setPendingResolutionNote] = useState<string | null>(null);

  const role = session?.user?.role ?? "";
  const canPerformAudit =
    role === "compliance_auditor" || role === "compliance_manager" || role === "admin";
  const canAddNotes = canPerformAudit;
  const canDelete = role === "admin" || role === "compliance_manager";
  const canManageTemplates = role === "admin" || role === "compliance_manager";
  const canReassignAuditor = role === "admin" || role === "compliance_manager";
  const canSendKickoff = role === "admin" || role === "compliance_manager";

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (isError) {
    return <ErrorState message="Failed to load audit" onRetry={() => refetch()} />;
  }

  if (!audit) return null;

  const status = audit.status;
  const isInteractive = canPerformAudit && status !== "complete";

  function getChecked(item: AuditItemRow): boolean {
    return localChecked[item.id] ?? item.checked;
  }

  function handleToggle(item: AuditItemRow) {
    if (!isInteractive) return;
    const updated = { ...localChecked, [item.id]: !getChecked(item) };
    setLocalChecked(updated);
    const allItems = audit.items.map((i) => ({
      id: i.id,
      checked: updated[i.id] ?? i.checked,
    }));
    updateItems.mutate(allItems);
  }

  const requiredItems = audit.items.filter((i) => !i.itemOptional);
  const totalItems = requiredItems.length;
  const checkedCount = requiredItems.filter((i) => getChecked(i)).length;
  const progressPercent = totalItems > 0 ? Math.round((checkedCount / totalItems) * 100) : 0;

  const uncheckedRequired = requiredItems.filter((i) => !getChecked(i)).length;
  const allRequiredChecked = uncheckedRequired === 0;

  const notesItem = notesItemId ? (audit.items.find((i) => i.id === notesItemId) ?? null) : null;

  const blockedNotes = audit.notes.filter(
    (n) => n.context === "blocked" || n.context === "resolution",
  );
  const generalNotes = audit.notes.filter((n) => n.context === "general");

  function handleStatusClick(s: (typeof ALL_STATUSES)[number]) {
    if (status === "blocked" && s !== "blocked") {
      setPendingStatus(s);
      setUnblockDialogOpen(true);
      return;
    }
    if (s === "blocked") {
      setBlockDialogOpen(true);
      return;
    }
    if (s === "complete") {
      setCompleteDialogOpen(true);
      return;
    }
    setStatus.mutate({ status: s });
  }

  async function handleUnblockConfirm(resolutionNote: string) {
    setUnblockDialogOpen(false);
    if (pendingStatus === "complete") {
      setPendingResolutionNote(resolutionNote);
      setCompleteDialogOpen(true);
    } else if (pendingStatus) {
      await setStatus.mutateAsync({ status: pendingStatus, resolutionNote });
    }
    setPendingStatus(null);
  }

  const showFindings = status === "complete" && !!audit.findings;

  return (
    <div className={showFindings ? "lg:grid lg:grid-cols-[48rem_26rem] gap-6 items-start" : ""}>
      <div className="space-y-6 max-w-3xl">
        {/* Assigned compliance auditor */}
        {audit.assignedTo && (
          <div className="relative flex items-center justify-between gap-4 overflow-hidden rounded-lg border border-border bg-card p-4 pl-6 shadow-sm">
            <div className="absolute inset-y-0 left-0 w-1 bg-purple-500" />
            <div className="flex items-center gap-4 min-w-0">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-lg font-semibold">
                {audit.assignedTo.name.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-primary">
                  Assigned Compliance Auditor
                  {canReassignAuditor && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-5 w-5 text-muted-foreground"
                      onClick={() => setEditAssigneeOpen(true)}
                      title="Reassign auditor"
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                  )}
                </p>
                <p className="text-lg font-bold leading-tight truncate">{audit.assignedTo.name}</p>
                <p className="text-sm text-muted-foreground truncate">{audit.assignedTo.email}</p>
              </div>
            </div>
            <div className="flex flex-col items-end gap-2 shrink-0">
              {audit.createdBy && (
                <div className="text-right">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Created By
                  </p>
                  <p className="text-base font-semibold">{audit.createdBy.name}</p>
                  <p className="text-xs text-muted-foreground">on {formatDate(audit.createdAt)}</p>
                </div>
              )}
              {canSendKickoff &&
                !audit.kickoffEmailSentAt &&
                (audit.guidanceRequestedAt || status === "guidance_requested") && (
                  <Button size="sm" variant="outline" onClick={() => setKickoffDialogOpen(true)}>
                    <Mail className="h-4 w-4 mr-1" />
                    Send Kickoff Email
                  </Button>
                )}
              {((canPerformAudit && status === "assigned") ||
                (canSendKickoff && status !== "assigned" && status !== "complete")) && (
                <Button size="sm" variant="outline" onClick={() => setGuidanceDialogOpen(true)}>
                  <MessageCircleQuestion className="h-4 w-4 mr-1" />
                  Request Guidance
                </Button>
              )}
            </div>
          </div>
        )}

        {canSendKickoff && audit.isStale && (
          <div
            role="alert"
            className="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm font-medium text-destructive"
          >
            <Flag className="h-4 w-4 shrink-0" />
            Needs attention: nothing on this audit has changed in {audit.idleDays} days.
          </div>
        )}

        {canSendKickoff && <GuidanceRequestLog auditId={auditId} />}

        {audit.kickoffEmailSentAt && (
          <Card>
            <CardContent>
              <p className="flex items-center gap-2 text-sm font-semibold">
                <Mail className="h-4 w-4 text-muted-foreground" />
                Kickoff email sent {formatDateTime(audit.kickoffEmailSentAt)}
                {audit.kickoffEmailSentBy && ` by ${audit.kickoffEmailSentBy.name}`}
                {role === "admin" && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="ml-auto"
                    onClick={() => setResendDialogOpen(true)}
                  >
                    Resend
                  </Button>
                )}
              </p>
              <dl className="mt-2 space-y-1 text-sm text-muted-foreground">
                <div>
                  <dt className="inline font-medium text-foreground">To: </dt>
                  <dd className="inline">{(audit.kickoffEmailTo ?? []).join("; ") || "—"}</dd>
                </div>
                {(audit.kickoffEmailCc ?? []).length > 0 && (
                  <div>
                    <dt className="inline font-medium text-foreground">CC: </dt>
                    <dd className="inline">{(audit.kickoffEmailCc ?? []).join("; ")}</dd>
                  </div>
                )}
                {(audit.kickoffEmailMissing ?? []).length > 0 && (
                  <div>
                    <dt className="inline font-medium text-foreground">Not sent to: </dt>
                    <dd className="inline">{(audit.kickoffEmailMissing ?? []).join("; ")}</dd>
                  </div>
                )}
              </dl>
              {audit.kickoffEmailBody && (
                <details className="mt-3 text-sm">
                  <summary className="cursor-pointer font-medium">Message as sent</summary>
                  <p className="mt-2 font-medium">{audit.kickoffEmailSubject}</p>
                  <RichTextEditor className="mt-1" readOnly value={audit.kickoffEmailBody} />
                </details>
              )}
            </CardContent>
          </Card>
        )}

        {/* Header */}
        <Card>
          <CardContent>
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <h2 className="text-xl font-bold">{audit.projectName}</h2>
                {audit.accountName && (
                  <p className="text-sm text-muted-foreground">{audit.accountName}</p>
                )}
                <p className="flex items-center gap-1 text-sm text-muted-foreground">
                  Templates: {audit.templates.map((t) => t.name).join(", ") || "—"}
                  {canManageTemplates && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-5 w-5 text-muted-foreground"
                      onClick={() => setEditTemplatesOpen(true)}
                      title="Edit templates"
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {project && (
                  <Button variant="ghost" size="icon" onClick={() => setTeamOpen(true)}>
                    <Users className="h-4 w-4" />
                    <span className="sr-only">View team</span>
                  </Button>
                )}
                <AuditFilesButton auditId={auditId} canUpload={canPerformAudit} />
                <Badge variant={AUDIT_STATUS_BADGE_VARIANTS[status] ?? "outline"}>
                  {AUDIT_STATUS_LABELS[status] ?? status}
                </Badge>
                {status === "blocked" && audit.blockedBy && (
                  <Badge variant="outline">{BLOCKED_BY_LABELS[audit.blockedBy]}</Badge>
                )}
                {canDelete && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground hover:text-destructive"
                    onClick={() => setDeleteOpen(true)}
                    title="Delete audit"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>

            {/* Progress */}
            {totalItems > 0 && (
              <div className="mt-4 space-y-1">
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>Progress</span>
                  <span>
                    {checkedCount} / {totalItems} ({progressPercent}%)
                  </span>
                </div>
                <div className="h-2 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full rounded-full bg-primary transition-all"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>
            )}

            {/* Auditor status controls */}
            {canPerformAudit && (
              <div className="mt-4 space-y-2">
                <p className="text-xs font-medium text-muted-foreground">Set status</p>
                <div className="flex flex-wrap gap-2">
                  {ALL_STATUSES.map((s) => {
                    const isCurrent = s === status;
                    const isCompleteBlocked = s === "complete" && !allRequiredChecked;
                    return (
                      <Button
                        key={s}
                        size="sm"
                        variant={isCurrent ? "default" : "outline"}
                        disabled={isCurrent || setStatus.isPending || isCompleteBlocked}
                        onClick={() => handleStatusClick(s)}
                        title={
                          isCompleteBlocked
                            ? `${uncheckedRequired} required item(s) must be checked first`
                            : undefined
                        }
                      >
                        {AUDIT_STATUS_LABELS[s]}
                      </Button>
                    );
                  })}
                </div>
                {!allRequiredChecked && (
                  <p className="text-xs text-muted-foreground">
                    Complete requires all {requiredItems.length} required item
                    {requiredItems.length !== 1 ? "s" : ""} to be checked ({uncheckedRequired}{" "}
                    remaining)
                  </p>
                )}
                {blockedNotes.length > 0 && (
                  <div className="space-y-2 pt-1">
                    <p className="text-xs font-medium text-muted-foreground">
                      Blocked note{blockedNotes.length !== 1 ? "s" : ""}
                    </p>
                    <div className="space-y-2">
                      {blockedNotes.map((note) => (
                        <BlockedNoteItem
                          key={note.id}
                          note={note}
                          auditId={auditId}
                          canEdit={note.authorId === session?.user?.id}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Checklist */}
        {audit.sections.map((section) => {
          const collapsed = isSectionCollapsed(section.sectionTitle);
          return (
            <Card key={section.sectionTitle}>
              <CardHeader
                className="pb-3 cursor-pointer select-none"
                role="button"
                tabIndex={0}
                aria-expanded={!collapsed}
                onClick={() => toggleSection(section.sectionTitle)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleSection(section.sectionTitle);
                  }
                }}
              >
                <CardTitle className="text-base flex items-center gap-2">
                  {collapsed ? (
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                  )}
                  {section.sectionTitle}
                </CardTitle>
              </CardHeader>
              {!collapsed && (
                <CardContent className="space-y-3">
                  {section.items.map((item) => (
                    <ChecklistItem
                      key={item.id}
                      auditId={auditId}
                      item={item}
                      files={auditFiles.filter((f) => f.sourceItemId === item.id)}
                      checked={getChecked(item)}
                      interactive={isInteractive}
                      canAddNote={canAddNotes}
                      canUpload={canPerformAudit}
                      onToggle={() => handleToggle(item)}
                      onOpenNotes={() => setNotesItemId(item.id)}
                    />
                  ))}
                </CardContent>
              )}
            </Card>
          );
        })}

        {/* Audit-level notes */}
        <AuditNotes
          auditId={auditId}
          notes={generalNotes}
          currentUserId={session?.user?.id ?? ""}
          canAdd={canAddNotes}
        />

        {/* Item notes dialog */}
        {notesItem && (
          <ItemNotesDialog
            open={!!notesItemId}
            onOpenChange={(open) => {
              if (!open) setNotesItemId(null);
            }}
            auditId={auditId}
            item={notesItem}
            canAdd={canAddNotes}
            currentUserId={session?.user?.id ?? ""}
          />
        )}

        <ConfirmDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          title="Delete Audit"
          description={`Are you sure you want to delete the audit for "${audit.projectName}"? This cannot be undone.`}
          confirmLabel="Delete"
          variant="destructive"
          loading={deleteAudit.isPending}
          onConfirm={async () => {
            await deleteAudit.mutateAsync(auditId);
            navigate({ to: "/dashboard/audits" });
          }}
        />

        {canManageTemplates && (
          <EditAuditTemplatesDialog
            open={editTemplatesOpen}
            onOpenChange={setEditTemplatesOpen}
            auditId={auditId}
            currentTemplates={audit.templates}
          />
        )}

        {canReassignAuditor && audit.assignedTo && (
          <EditAuditAssigneeDialog
            open={editAssigneeOpen}
            onOpenChange={setEditAssigneeOpen}
            auditId={auditId}
            currentAssigneeId={audit.assignedTo.id}
          />
        )}

        {project && (
          <ProjectTeamDialog open={teamOpen} onOpenChange={setTeamOpen} project={project} />
        )}

        <BlockAuditDialog
          open={blockDialogOpen}
          onOpenChange={setBlockDialogOpen}
          isPending={setStatus.isPending}
          onConfirm={async (reason, blockedBy) => {
            await setStatus.mutateAsync({ status: "blocked", blockedReason: reason, blockedBy });
            setBlockDialogOpen(false);
          }}
        />

        <CompleteAuditDialog
          open={completeDialogOpen}
          onOpenChange={setCompleteDialogOpen}
          isPending={setStatus.isPending}
          onConfirm={async (findings) => {
            await setStatus.mutateAsync({
              status: "complete",
              recordedInPmApp: true,
              findings,
              resolutionNote: pendingResolutionNote ?? undefined,
            });
            setPendingResolutionNote(null);
            setCompleteDialogOpen(false);
          }}
        />

        <UnblockAuditDialog
          open={unblockDialogOpen}
          onOpenChange={(open) => {
            setUnblockDialogOpen(open);
            if (!open) setPendingStatus(null);
          }}
          isPending={setStatus.isPending}
          onConfirm={handleUnblockConfirm}
        />

        <KickoffEmailDialog
          open={kickoffDialogOpen}
          onOpenChange={setKickoffDialogOpen}
          auditId={auditId}
        />
        <KickoffEmailDialog
          resend
          open={resendDialogOpen}
          onOpenChange={setResendDialogOpen}
          auditId={auditId}
        />

        {audit.assignedTo && (
          <RequestGuidanceForAuditDialog
            open={guidanceDialogOpen}
            onOpenChange={setGuidanceDialogOpen}
            auditId={auditId}
            projectName={audit.projectName}
            demandName={project?.demand}
            auditorName={audit.assignedTo.name}
            templateName={audit.templates.map((t) => t.name).join(", ") || "—"}
            keepsStatus={status !== "assigned"}
          />
        )}
      </div>

      {showFindings && (
        <Card className="lg:sticky lg:top-6">
          <CardHeader>
            <CardTitle className="text-base">Audit Findings & Recommendations</CardTitle>
            <CardDescription>
              {audit.completedBy?.name ?? "Unknown"} · {formatDateTime(audit.updatedAt)}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap break-words">{audit.findings}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ── Item notes dialog ──────────────────────────────────────────────────────

function ItemNotesDialog({
  open,
  onOpenChange,
  auditId,
  item,
  canAdd,
  currentUserId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  auditId: string;
  item: AuditItemRow;
  canAdd: boolean;
  currentUserId: string;
}) {
  const { data: notes = [], isLoading } = useAuditItemNotes(auditId, open ? item.id : null);
  const createNote = useCreateAuditItemNote(auditId, item.id);
  const [noteText, setNoteText] = useState("");

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!noteText.trim()) return;
    createNote.mutate(noteText.trim(), {
      onSuccess: () => {
        setNoteText("");
        onOpenChange(false);
      },
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-base leading-snug pr-6">{item.itemText}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-3 py-2 min-h-0">
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
            </div>
          ) : notes.length === 0 ? (
            <p className="text-sm text-muted-foreground">No notes yet.</p>
          ) : (
            notes.map((note) => (
              <ItemNoteItem
                key={note.id}
                note={note}
                auditId={auditId}
                itemId={item.id}
                canEdit={note.authorId === currentUserId}
              />
            ))
          )}
        </div>

        {canAdd && (
          <form onSubmit={handleSubmit} className="space-y-2 border-t pt-3">
            <Textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Add a note…"
              rows={3}
              className="text-sm"
            />
            <div className="flex justify-end">
              <Button type="submit" size="sm" disabled={!noteText.trim() || createNote.isPending}>
                {createNote.isPending ? "Adding…" : "Add Note"}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ItemNoteItem({
  note,
  auditId,
  itemId,
  canEdit,
}: {
  note: AuditItemNoteRow;
  auditId: string;
  itemId: string;
  canEdit: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(note.body);
  const updateNote = useUpdateAuditItemNote(auditId, itemId);
  const deleteNote = useDeleteAuditItemNote(auditId, itemId);

  useEffect(() => {
    setBody(note.body);
  }, [note.body]);

  function handleCancel() {
    setEditing(false);
    setBody(note.body);
  }

  function handleSave() {
    const trimmed = body.trim();
    if (!trimmed || trimmed === note.body) {
      handleCancel();
      return;
    }
    updateNote.mutate({ noteId: note.id, body: trimmed }, { onSuccess: () => setEditing(false) });
  }

  return (
    <div className="rounded-md border border-border bg-muted/30 p-3 space-y-1">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground">
          {note.authorName ?? "Unknown"} · {formatDateTime(note.createdAt)}
        </span>
        {canEdit && !editing && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="text-muted-foreground hover:text-foreground transition-colors"
              title="Edit note"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => deleteNote.mutate(note.id)}
              disabled={deleteNote.isPending}
              className="text-muted-foreground hover:text-destructive transition-colors"
              title="Delete note"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>
      {editing ? (
        <div className="space-y-2">
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            className="text-sm bg-background"
            autoFocus
          />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={handleCancel}>
              Cancel
            </Button>
            <Button type="button" size="sm" disabled={updateNote.isPending} onClick={handleSave}>
              {updateNote.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-sm whitespace-pre-wrap break-words">{note.body}</p>
      )}
    </div>
  );
}

// ── Blocked / resolution notes ─────────────────────────────────────────────

function BlockedNoteItem({
  note,
  auditId,
  canEdit,
}: {
  note: AuditNoteRow;
  auditId: string;
  canEdit: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(note.body);
  const updateNote = useUpdateAuditNote(auditId);

  useEffect(() => {
    setBody(note.body);
  }, [note.body]);

  function handleCancel() {
    setEditing(false);
    setBody(note.body);
  }

  function handleSave() {
    const trimmed = body.trim();
    if (!trimmed || trimmed === note.body) {
      handleCancel();
      return;
    }
    updateNote.mutate({ noteId: note.id, body: trimmed }, { onSuccess: () => setEditing(false) });
  }

  return (
    <div
      className={
        note.context === "resolution"
          ? "rounded-md border border-emerald-200 bg-emerald-50 p-3 space-y-1 dark:border-emerald-900/50 dark:bg-emerald-950/30"
          : "rounded-md border border-red-200 bg-red-50 p-3 space-y-1 dark:border-red-900/50 dark:bg-red-950/30"
      }
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground">
          {note.authorName ?? "Unknown"} · {formatDateTime(note.createdAt)}
        </span>
        {canEdit && !editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-muted-foreground hover:text-foreground transition-colors"
            title="Edit note"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {editing ? (
        <div className="space-y-2">
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            className="text-sm bg-background"
            autoFocus
          />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={handleCancel}>
              Cancel
            </Button>
            <Button type="button" size="sm" disabled={updateNote.isPending} onClick={handleSave}>
              {updateNote.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-sm whitespace-pre-wrap break-words">{note.body}</p>
      )}
    </div>
  );
}

// ── Audit-level notes ──────────────────────────────────────────────────────

function AuditNoteItem({
  note,
  auditId,
  canEdit,
  onDelete,
  deletePending,
}: {
  note: AuditNoteRow;
  auditId: string;
  canEdit: boolean;
  onDelete: () => void;
  deletePending: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(note.body);
  const updateNote = useUpdateAuditNote(auditId);

  useEffect(() => {
    setBody(note.body);
  }, [note.body]);

  function handleCancel() {
    setEditing(false);
    setBody(note.body);
  }

  function handleSave() {
    const trimmed = body.trim();
    if (!trimmed || trimmed === note.body) {
      handleCancel();
      return;
    }
    updateNote.mutate({ noteId: note.id, body: trimmed }, { onSuccess: () => setEditing(false) });
  }

  return (
    <div className="rounded-md border border-border bg-muted/30 p-3 space-y-1">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground">
          {note.authorName ?? "Unknown"} · {formatDateTime(note.createdAt)}
        </span>
        {canEdit && !editing && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="text-muted-foreground hover:text-foreground transition-colors"
              title="Edit note"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={onDelete}
              disabled={deletePending}
              className="text-muted-foreground hover:text-destructive transition-colors"
              title="Delete note"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>
      {editing ? (
        <div className="space-y-2">
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            className="text-sm bg-background"
            autoFocus
          />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={handleCancel}>
              Cancel
            </Button>
            <Button type="button" size="sm" disabled={updateNote.isPending} onClick={handleSave}>
              {updateNote.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-sm whitespace-pre-wrap break-words">{note.body}</p>
      )}
    </div>
  );
}

function AuditNotes({
  auditId,
  notes,
  currentUserId,
  canAdd,
}: {
  auditId: string;
  notes: AuditNoteRow[];
  currentUserId: string;
  canAdd: boolean;
}) {
  const [noteText, setNoteText] = useState("");
  const createNote = useCreateAuditNote(auditId);
  const deleteNote = useDeleteAuditNote(auditId);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!noteText.trim()) return;
    createNote.mutate(noteText.trim(), {
      onSuccess: () => setNoteText(""),
    });
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Notes</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {canAdd && (
          <form onSubmit={handleSubmit} className="space-y-2">
            <Textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Add a note…"
              rows={3}
              className="text-sm"
            />
            <div className="flex justify-end">
              <Button type="submit" size="sm" disabled={!noteText.trim() || createNote.isPending}>
                {createNote.isPending ? "Adding…" : "Add Note"}
              </Button>
            </div>
          </form>
        )}

        {notes.length === 0 ? (
          <p className="text-sm text-muted-foreground">No notes yet.</p>
        ) : (
          <div className="space-y-3">
            {notes.map((note) => (
              <AuditNoteItem
                key={note.id}
                note={note}
                auditId={auditId}
                canEdit={note.authorId === currentUserId}
                onDelete={() => deleteNote.mutate(note.id)}
                deletePending={deleteNote.isPending}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Checklist item ─────────────────────────────────────────────────────────

interface ChecklistItemProps {
  auditId: string;
  item: AuditItemRow;
  files: AuditFileRow[];
  checked: boolean;
  interactive: boolean;
  canAddNote: boolean;
  canUpload: boolean;
  onToggle: () => void;
  onOpenNotes: () => void;
}

function ChecklistItem({
  auditId,
  item,
  files,
  checked,
  interactive,
  canAddNote,
  canUpload,
  onToggle,
  onOpenNotes,
}: ChecklistItemProps) {
  const { latestNote, noteCount } = item;

  async function handleDownload(file: AuditFileRow) {
    try {
      await openAuditFileDownload(auditId, file.id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to download attachment");
    }
  }

  return (
    <div className="space-y-1.5">
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={onToggle}
          disabled={!interactive}
          className="mt-0.5 shrink-0 text-primary disabled:text-muted-foreground"
        >
          {checked ? <CheckSquare className="h-5 w-5" /> : <Square className="h-5 w-5" />}
        </button>
        <div className="flex-1 min-w-0">
          <span className={`text-sm ${checked ? "line-through text-muted-foreground" : ""}`}>
            {item.itemText}
          </span>
          {item.itemHelpText && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="ml-1.5 inline-flex align-middle text-muted-foreground hover:text-foreground"
                >
                  <HelpCircle className="h-3.5 w-3.5" />
                  <span className="sr-only">Help</span>
                </button>
              </TooltipTrigger>
              <TooltipContent>{item.itemHelpText}</TooltipContent>
            </Tooltip>
          )}
          {item.itemOptional && (
            <Badge variant="secondary" className="ml-2 text-xs">
              optional
            </Badge>
          )}
        </div>
      </div>

      {/* Latest note preview + actions */}
      <div className="ml-8 space-y-1">
        {latestNote && (
          <div className="rounded bg-muted/50 px-2.5 py-1.5 space-y-0.5">
            <span className="text-xs text-muted-foreground">
              {latestNote.authorName ?? "Unknown"} · {formatDateTime(latestNote.createdAt)}
            </span>
            <p className="text-xs whitespace-pre-wrap line-clamp-2 break-words">
              {latestNote.body}
            </p>
          </div>
        )}

        <div className="flex items-center gap-3">
          {(canAddNote || noteCount > 0) && (
            <button
              type="button"
              onClick={onOpenNotes}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <MessageSquare className="h-3 w-3" />
              {noteCount === 0
                ? canAddNote
                  ? "Add note"
                  : null
                : noteCount === 1
                  ? "1 note"
                  : `${noteCount} notes`}
            </button>
          )}
          {noteCount > 1 && latestNote && (
            <span className="text-xs text-muted-foreground">· most recent shown</span>
          )}
          {item.itemUpload && canUpload && <ItemUploadButton auditId={auditId} itemId={item.id} />}
        </div>

        {files.length > 0 && (
          <div className="flex flex-col items-start gap-1">
            {files.map((file) => (
              <button
                key={file.id}
                type="button"
                onClick={() => handleDownload(file)}
                className="flex items-center gap-1 text-xs text-primary hover:underline"
              >
                <Paperclip className="h-3 w-3 shrink-0" />
                {file.fileName}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
